import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
/** Components */
import { DefaultTable } from 'Components/DefaultTable';
import { PageHelper } from 'Components/PageHelper';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { buildTableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
/** Services */
import { getSystemsShortPaged } from 'services/systems-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Box
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getUsersName } from 'services/users-service';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import { InputLoader } from 'Components/PageLoader/Input';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import { ModalConfirm } from 'Components/ModalConfirm';

export const Systems = () => {
  const { t } = useTranslation(['mapping', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const tableHeader = useMemo(() => buildTableHeader(t), [t]);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      developedInHouse: 'Todos',
      saas: 'Todos',
      user: '',
      archived: false,
    },
  });

  const getContentSearch = () => {
    const dados = getValues();
    let queryString = `&archived=${dados.archived}&developedInHouse=${dados.developedInHouse}&saas=${dados.saas}`;
    if (dados.name !== '') {
      queryString += `&name=${dados.name}`;
    }
    if (dados.user !== '') {
      queryString += `&user=${dados.user}`;
    }
    if (dados.criticity !== '') {
      queryString += `&criticity=${dados.criticity}`;
    }

    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const {
    isLoading,
    error,
    data: pagedSystems,
  } = useQuery(['getSystems', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];

    const queryContent = getContentSearch();
    const result = await getSystemsShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };

  const onArchive = item => {
    setItemToArchive(item);
    setOpenArchiveModal(true);
  };

  const onCloseArchiveModal = () => {
    setOpenArchiveModal(false);
    setItemToArchive(null);
  };

  const onUnarchive = item => {
    setItemToUnarchive(item);
    setOpenUnarchiveModal(true);
  };

  const onCloseUnarchiveModal = () => {
    setOpenUnarchiveModal(false);
    setItemToUnarchive(null);
  };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseDeleteModal();
      SwalReact.fire({
        title: t('mapping:systems.toasts.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseArchiveModal();
      SwalReact.fire({
        title: t('mapping:systems.toasts.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseUnarchiveModal();
      SwalReact.fire({
        title: t('mapping:systems.toasts.errorTitle'),
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const tourSteps = [
    {
      target: 'body',
      content: t('mapping:systems.tour.list.welcome'),
      placement: 'center',
      disableBeacon: true,
    },
    { target: '[data-tour="page-title"]', content: t('mapping:systems.tour.list.pageTitle'), disableScrolling: true },
    { target: '[data-tour="add-button"]', content: t('mapping:systems.tour.list.addButton'), disableScrolling: true },
    { target: '[data-tour="info-card"]', content: t('mapping:systems.tour.list.infoCard'), disableScrolling: true },
    { target: '[data-tour="filter-card"]', content: t('mapping:systems.tour.list.filterCard'), disableScrolling: true },
    { target: '[data-tour="filter-name"]', content: t('mapping:systems.tour.list.filterName') },
    { target: '[data-tour="filter-user"]', content: t('mapping:systems.tour.list.filterUser') },
    { target: '[data-tour="filter-criticity"]', content: t('mapping:systems.tour.list.filterCriticity') },
    { target: '[data-tour="filter-developed"]', content: t('mapping:systems.tour.list.filterDeveloped') },
    { target: '[data-tour="filter-saas"]', content: t('mapping:systems.tour.list.filterSaas') },
    { target: '[data-tour="filter-archived"]', content: t('mapping:systems.tour.list.filterArchived') },
    { target: '[data-tour="filter-buttons"]', content: t('mapping:systems.tour.list.filterButtons') },
    { target: '[data-tour="table"]', content: t('mapping:systems.tour.list.table') },
    { target: '[data-tour="pagination"]', content: t('mapping:systems.tour.list.pagination') },
    { target: 'body', content: t('mapping:systems.tour.list.done'), placement: 'center' },
  ];

  const filterOptions = [
    { value: 'Todos', label: t('mapping:systems.list.filters.options.all') },
    { value: 'Sim', label: t('mapping:systems.list.filters.options.yes') },
    { value: 'Não', label: t('mapping:systems.list.filters.options.no') },
  ];

  const getCriticityColor = (level) => {
    const colors = { '1': '#22c55e', '2': '#84cc16', '3': '#eab308', '4': '#f97316', '5': '#ef4444' };
    return colors[level] || '#6b7280';
  };

  const getCriticityLabel = (level) => t(`mapping:systems.criticality.${level}`, { defaultValue: level });

  return (
    <Container title={t('mapping:systems.list.title')} breadcrumb={t('mapping:systems.list.breadcrumb')} showButtonAdd linkPage="/systems" dataTour="page-title" dataTourAdd="add-button">
      <div data-tour="info-card">
        <Information
          title={t('mapping:systems.list.cardTitle')}
          description={
            <p className="m-0">
              {t('mapping:systems.list.description')}
            </p>
          }
          style={{
            marginTop: '24px',
          }}
        />
      </div>
      {!error && (
        <>
          <Card className="mb-2 mt-3" data-tour="filter-card">
            <Row className="mb-3">
              <Column xs={12} md={6} className="mb-2 mb-md-0">
                <div data-tour="filter-name" style={{ width: '100%' }}>
                  <CustomTextField
                    {...register('name')}
                    label={t('mapping:systems.list.filters.name')}
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />                </div>              </Column>
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-user" style={{ width: '100%' }}>
                  {!userLoading && (
                    <Controller
                    control={control}
                    name="user"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="user-label" shrink>
                          {t('mapping:systems.list.filters.responsible')}
                        </InputLabel>
                        <Select
                          labelId="user-label"
                          id="user"
                          value={value}
                          label={t('mapping:systems.list.filters.responsible')}
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            {t('mapping:systems.list.filters.selectResponsible')}
                          </MenuItem>
                          {users
                            ?.map(user => user.name)
                            .map((item, index) => (
                              <MenuItem value={item} key={`user-${index}`}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
                {userLoading && (
                  <FormControl fullWidth sx={{ margin: '0 auto' }}>
                    <InputLabel id="user-label" shrink={true}>
                      {t('mapping:systems.list.filters.responsible')}
                    </InputLabel>
                    <InputLoader height={'40px'} />
                  </FormControl>
                )}
                </div>
              </Column>
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-criticity" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="criticity"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="criticity-label" shrink>
                        {t('mapping:systems.list.filters.criticality')}
                      </InputLabel>
                      <Select
                        labelId="criticity-label"
                        id="criticity"
                        value={value}
                        label={t('mapping:systems.list.filters.criticality')}
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                        renderValue={(selected) => {
                          if (!selected) return t('mapping:systems.list.filters.selectCriticality');

                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: getCriticityColor(selected),
                                  border: '1px solid rgba(0,0,0,0.1)'
                                }}
                              />
                              <span>{getCriticityLabel(selected)}</span>
                            </div>
                          );
                        }}
                      >
                        <MenuItem value="" key={-1}>
                          {t('mapping:systems.list.filters.selectCriticality')}
                        </MenuItem>
                        {['1', '2', '3', '4', '5'].map((item, index) => (
                          <MenuItem value={item} key={index}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: getCriticityColor(item),
                                  border: '1px solid rgba(0,0,0,0.1)'
                                }}
                              />
                              <span>{getCriticityLabel(item)}</span>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
            </Row>
            <Row className="mb-2">
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-developed" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="developedInHouse"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="developed-in-house-label" shrink>
                        {t('mapping:systems.list.filters.developedInHouse')}
                      </InputLabel>
                      <Select
                        labelId="developed-in-house-label"
                        id="developedInHouse"
                        value={value}
                        label={t('mapping:systems.list.filters.developedInHouse')}
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {filterOptions.map(opt => (
                          <MenuItem value={opt.value} key={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={2} className="mb-2 mb-md-0">
                <div data-tour="filter-saas" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="saas"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="saas-label" shrink>
                        {t('mapping:systems.list.filters.saas')}
                      </InputLabel>
                      <Select
                        labelId="saas-label"
                        id="saas"
                        value={value}
                        label={t('mapping:systems.list.filters.saas')}
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {filterOptions.map(opt => (
                          <MenuItem value={opt.value} key={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={1} className="mb-2 mb-md-0">
                <div data-tour="filter-archived" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="archived"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={<Checkbox checked={value} onChange={onChange} name="archived" />}
                      label={t('mapping:systems.list.filters.archived')}
                      sx={{ marginLeft: 0, width: 'fit-content' }}
                    />
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={2} className="mb-2 mb-md-0">
                <Box display="flex" gap={1} data-tour="filter-buttons">
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: '12px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      width: '100%',
                      '@media (min-width: 768px)': {
                        width: 'fit-content'
                      }
                    }}
                    onClick={() => {
                      reset();
                      setRefetch(!refetch);
                    }}
                  >
                    {t('mapping:systems.list.filters.clear')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      borderRadius: '12px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      width: '100%',
                      '@media (min-width: 768px)': {
                        width: 'fit-content'
                      }
                    }}
                    endIcon={<SearchOutlinedIcon />}
                    disabled={watch('typeFilter') === ''}
                    onClick={() => {
                      setRefetch(!refetch);
                    }}
                  >
                    {t('mapping:systems.list.filters.filter')}
                  </Button>
                </Box>
              </Column>

            </Row>
          </Card>
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('mapping:systems.list.errorLoad')}</div>}
      {!isLoading && !error && pagedSystems.data?.length === 0 && (
        <NoData description={t('mapping:systems.list.emptyState')} />
      )}
      {!isLoading && !error && pagedSystems.data?.length > 0 && (
        <div className="container">
          <div data-tour="table">
            <DefaultTable
            rows={tableHeader}
            cells={pagedSystems.data}
            onClickDelete={onDelete}
            onClickArchive={onArchive}
            onClickUnarchive={onUnarchive}
            showArchive
            link="systems"
          />
          </div>
          <div data-tour="pagination">
            <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
              <Pagination
              count={pagedSystems.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={t('mapping:systems.list.modals.deleteMessage', { name: itemToDelete?.name ?? '' })}
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <ModalConfirm
            description={t('mapping:systems.list.modals.archiveMessage', { name: itemToArchive?.name ?? '' })}
            open={openArchiveModal}
            onClose={onCloseArchiveModal}
            onConfirm={onConfirmArchiveModal}
          />
          <ModalConfirm
            description={t('mapping:systems.list.modals.unarchiveMessage', { name: itemToUnarchive?.name ?? '' })}
            open={openUnarchiveModal}
            onClose={onCloseUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="systems-list" />
    </Container>
  );
};
